// Liste der Übergabeprotokolle im Widget-Stil (design_handoff_uebergabe §2): Kopf, Art-Widgets,
// Filterzeile, Karte. Welche Chips/Aktionen erscheinen, entscheidet kartenAnsicht (lib/uebergabeAnsicht),
// zusammengesetzt aus den bestehenden Regeln — hier nur Darstellung.
import type { CSSProperties, ReactNode } from 'react';
import {
  ArrowsClockwise, CheckCircle, CircleNotch, Clock, CloudArrowUp, Copy, Devices, DeviceMobile, DownloadSimple, EnvelopeSimple,
  FilePdf, FolderOpen, FolderSimplePlus, Info, Key, MagnifyingGlass, NotePencil, PaperPlaneTilt, PencilSimple, Plus, SignIn,
  SignOut, Stack, Trash, WarningCircle, X, XCircle, type Icon,
} from '@phosphor-icons/react';
import { StatusKachel } from '../../../../components/ui/StatusKachel';
import { Segment } from '../../../../components/ui/Segment';
import { ART_FARBE, ART_LABEL, type ArtOderAlle, type InfoChip, type KartenAktion, type KartenAnsicht } from '../../../../lib/uebergabeAnsicht/darstellung';
import type { Art, ProtokollStatus } from '../../../../lib/uebergabe/typen';

export const ART_ICON: Record<Art, Icon> = { einzug: SignIn, auszug: SignOut, kauf: Key };

const ART_REIHENFOLGE: Art[] = ['einzug', 'auszug', 'kauf'];

/** Art der Übergabe als drei Kacheln (Neue Übergabe, Schritt 1): aktiv = Ring + Fläche in Art-Farbe. */
/** `gesperrt`: nur anzeigen (Hausmeister-App – Art legt die Verwaltung fest). */
export function ArtKacheln({ wert, onWahl, gesperrt = false }: { wert: Art | null; onWahl: (a: Art) => void; gesperrt?: boolean }) {
  return (
    <div role="radiogroup" aria-label="Art der Übergabe" className="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))' }}>
      {ART_REIHENFOLGE.map((a) => {
        const an = wert === a;
        const farbe = ART_FARBE[a];
        const I = ART_ICON[a];
        return (
          <button key={a} type="button" role="radio" aria-checked={an} onClick={() => onWahl(a)} disabled={gesperrt && !an}
            className="flex flex-col items-center justify-center text-center"
            style={{ minHeight: 96, padding: '14px 10px', gap: 8, borderRadius: 20, border: 'none', cursor: gesperrt ? 'default' : 'pointer', opacity: gesperrt && !an ? 0.45 : 1, fontFamily: 'inherit', fontSize: 14, fontWeight: 800, color: 'var(--ink)',
              background: an ? `color-mix(in srgb, ${farbe} 14%, var(--glass))` : 'var(--glass)',
              boxShadow: an ? `inset 0 0 0 2px ${farbe}, var(--glass-sh)` : 'var(--glass-sh)' }}>
            <span aria-hidden className="inline-flex items-center justify-center"
              style={{ width: 36, height: 36, borderRadius: 11, background: an ? farbe : `color-mix(in srgb, ${farbe} 15%, transparent)`, color: an ? '#fff' : farbe }}>
              <I size={19} weight="fill" />
            </span>
            {ART_LABEL[a]}
          </button>
        );
      })}
    </div>
  );
}

export function ListenKopf({ anzahl, mobil, onNeu }: { anzahl: number; mobil: boolean; onNeu: () => void }) {
  return (
    <header className="flex flex-wrap items-end justify-between" style={{ gap: 14 }}>
      <div className="flex min-w-0 flex-col" style={{ gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink3)' }}>Vermietung</span>
        <h1 className="m-0" style={{ fontSize: mobil ? 30 : 40, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.02 }}>Übergabeprotokolle</h1>
        <span style={{ fontSize: 14, color: 'var(--ink2)' }}>Protokolle gesamt: {anzahl}</span>
      </div>
      <button type="button" onClick={onNeu} data-tip="Neue Übergabe – Art, Objekt und Einheit wählen und die Erfassung vor Ort starten"
        className="inline-flex items-center" style={{ height: 44, padding: '0 18px', gap: 8, borderRadius: 22, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 14, fontWeight: 800, background: 'var(--acc-fill)', boxShadow: 'var(--acc-sh)', color: 'var(--acc-ink)' }}>
        <Plus size={15} weight="bold" /> Neue Übergabe
      </button>
    </header>
  );
}

export function ArtWidgets({ zaehler, aktiv, onWahl }: { zaehler: Record<ArtOderAlle, number>; aktiv: ArtOderAlle; onWahl: (a: ArtOderAlle) => void }) {
  const eintraege: { key: ArtOderAlle; label: string; icon: Icon; farbe: string }[] = [
    { key: 'alle', label: 'Alle', icon: Stack, farbe: 'var(--acc)' },
    ...(['einzug', 'auszug', 'kauf'] as const).map((a) => ({ key: a, label: ART_LABEL[a], icon: ART_ICON[a], farbe: ART_FARBE[a] })),
  ];
  return (
    <div role="group" aria-label="Art" className="grid" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
      {eintraege.map((e) => (
        <StatusKachel key={e.key} label={e.label} zahl={zaehler[e.key]} farbe={e.farbe} icon={e.icon} aktiv={aktiv === e.key} wasserzeichen
          onClick={() => onWahl(e.key)} tip={`Art: ${e.label} – ${zaehler[e.key]} Protokolle in der aktuellen Auswahl`} />
      ))}
    </div>
  );
}

export function FilterZeile({ suche, onSuche, status, onStatus, mobil }: {
  suche: string; onSuche: (s: string) => void; status: ProtokollStatus | 'alle'; onStatus: (s: ProtokollStatus | 'alle') => void; mobil: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center" style={{ gap: 10 }}>
      <label className="immo-glas relative flex min-w-0 items-center" style={{ flex: '1 1 260px', height: 44, borderRadius: 22, padding: '0 8px 0 14px', gap: 8 }}>
        <MagnifyingGlass size={16} color="var(--ink3)" className="shrink-0" />
        <input value={suche} onChange={(e) => onSuche(e.target.value)} placeholder="Suchen…" aria-label="Suchen"
          className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }} />
        {suche && (
          <button type="button" onClick={() => onSuche('')} aria-label="Suche leeren" data-tip="Suche leeren" className="inline-flex shrink-0 items-center justify-center"
            style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink2)' }}>
            <X size={12} weight="bold" />
          </button>
        )}
      </label>
      <Segment ariaLabel="Status" art="akzent" hoehe={mobil ? 44 : 34} wert={status} onChange={onStatus}
        style={{ padding: 4, borderRadius: 22, background: 'var(--glass)', boxShadow: 'var(--glass-sh)' }}
        eintraege={[
          { key: 'alle', label: 'Alle', tip: 'Status: Alle' },
          { key: 'in_erfassung', label: 'In Erfassung', tip: 'Status: In Erfassung' },
          { key: 'abgeschlossen', label: 'Abgeschlossen', tip: 'Status: Abgeschlossen' },
        ]} />
    </div>
  );
}

function Chip({ farbe, flaeche, icon: I, children, tip }: { farbe: string; flaeche?: string; icon?: Icon; children: ReactNode; tip?: string }) {
  return (
    <span data-tip={tip} className="inline-flex shrink-0 items-center whitespace-nowrap"
      style={{ height: 24, padding: '0 9px', gap: 5, borderRadius: 12, fontSize: 11.5, fontWeight: 800,
        background: flaeche ?? `color-mix(in srgb, ${farbe} 14%, transparent)`, color: farbe }}>
      {I && <I size={12} weight="fill" />}{children}
    </span>
  );
}

const GERAET_ICON: Record<string, Icon> = { 'auf diesem Gerät': DeviceMobile, 'wartet auf Upload': CloudArrowUp, 'auf einem anderen Gerät': Devices };

const INFO_CHIP: Record<InfoChip, { label: string; farbe: string; icon: Icon }> = {
  versendet: { label: 'Versendet', farbe: 'var(--ok)', icon: EnvelopeSimple },
  pdf_fehlt: { label: 'PDF fehlt', farbe: 'var(--bad)', icon: FilePdf },
  nicht_versendet: { label: 'Nicht versendet', farbe: 'var(--warn)', icon: EnvelopeSimple },
  keine_adresse: { label: 'Keine Empfängeradresse hinterlegt', farbe: 'var(--warn)', icon: EnvelopeSimple },
  im_dms: { label: 'Im DMS', farbe: 'var(--info)', icon: FolderOpen },
};

const AKTION: Record<KartenAktion, { label: string; icon: Icon; art: 'fill' | 'akzent' | 'bad'; tip?: string }> = {
  weiter: { label: 'Weiter bearbeiten', icon: PencilSimple, art: 'fill', tip: 'Weiter bearbeiten – Erfassung fortsetzen' },
  senden: { label: 'Jetzt senden', icon: PaperPlaneTilt, art: 'akzent', tip: 'Jetzt senden – Protokoll abschließen, PDF erzeugen und versenden' },
  hier_weiter: { label: 'Hier weiterbearbeiten', icon: PencilSimple, art: 'akzent' },
  erneut_erzeugen: { label: 'Erneut erzeugen', icon: ArrowsClockwise, art: 'fill', tip: 'Erneut erzeugen – PDF neu erstellen und, wenn möglich, versenden' },
  mail_erneut: { label: 'Mail erneut senden', icon: EnvelopeSimple, art: 'fill' },
  pdf: { label: 'PDF', icon: DownloadSimple, art: 'fill', tip: 'PDF – Protokoll herunterladen' },
  ins_dms: { label: 'Ins DMS', icon: FolderSimplePlus, art: 'fill', tip: 'Ins DMS – PDF im Aktenschrank ablegen (für Protokolle vor der automatischen Ablage)' },
  als_vorlage: { label: 'Als Vorlage', icon: Copy, art: 'fill', tip: 'Als Vorlage – Dieselbe Wohnung, nächste Übergabe: Räume, Zustände und Fotos übernehmen' },
  verwerfen: { label: 'Lokalen Entwurf verwerfen', icon: Trash, art: 'fill' },
  abbrechen: { label: 'Abbrechen', icon: XCircle, art: 'bad', tip: 'Abbrechen – Übergabe als abgebrochen markieren' },
  entwurf_loeschen: { label: 'Entwurf löschen', icon: Trash, art: 'bad' },
};

export interface KartenProps {
  art: Art;
  titel: string;
  datum: string;
  parteien: string;
  ansicht: KartenAnsicht;
  /** Zeitpunkt „Seit …“ bei laufender Erfassung (bereits formatiert) */
  seit: string | null;
  erfasstVon: string | null;
  veraltetHinweis: string | null;
  versendetAm: string | null;
  hausmeister: ReactNode;
  /** je Aktion: gesperrt / läuft */
  zustand: (a: KartenAktion) => { gesperrt: boolean; laeuft: boolean; tip?: string };
  onAktion: (a: KartenAktion) => void;
  mobil: boolean;
}

export function UebergabeKarte({ art, titel, datum, parteien, ansicht, seit, erfasstVon, veraltetHinweis, versendetAm, hausmeister, zustand, onAktion, mobil }: KartenProps) {
  const farbe = ART_FARBE[art];
  const I = ART_ICON[art];
  const hinweis = !!ansicht.bereitGrund || !!veraltetHinweis;
  const zeigeInfo = ansicht.inErfassung || hinweis;
  const GI = ansicht.geraet ? GERAET_ICON[ansicht.geraet] ?? DeviceMobile : null;

  const knopf = (a: KartenAktion) => {
    const d = AKTION[a];
    const z = zustand(a);
    const AI = z.laeuft ? CircleNotch : d.icon;
    const stil: CSSProperties = d.art === 'akzent'
      ? { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: z.gesperrt ? 'none' : 'var(--acc-sh)' }
      : d.art === 'bad' ? { background: 'var(--bad-soft)', color: 'var(--bad)' } : { background: 'var(--fill2)', color: 'var(--ink)' };
    return (
      <button key={a} type="button" onClick={() => onAktion(a)} disabled={z.gesperrt} data-tip={z.tip ?? d.tip}
        className="inline-flex items-center" style={{ height: mobil ? 44 : 40, padding: '0 14px', gap: 7, borderRadius: 13, border: 'none',
          cursor: z.gesperrt ? 'default' : 'pointer', fontFamily: 'inherit', fontSize: 13, fontWeight: 800, opacity: z.gesperrt ? 0.4 : 1, ...stil }}>
        <AI size={15} weight="bold" className={z.laeuft ? 'animate-spin' : undefined} /> {d.label}
      </button>
    );
  };

  return (
    <article className="immo-glas flex min-w-0 flex-col" style={{ borderRadius: 24, padding: 16, gap: 12, opacity: ansicht.status === 'abgebrochen' ? 0.62 : 1 }}>
      <div className="flex min-w-0 items-start" style={{ gap: 12 }}>
        <span aria-hidden className="inline-flex shrink-0 items-center justify-center"
          style={{ width: 46, height: 46, borderRadius: 14, background: farbe, color: '#fff', boxShadow: `0 8px 18px -8px ${farbe}` }}>
          <I size={22} weight="fill" />
        </span>
        <div className="flex min-w-0 flex-1 flex-col" style={{ gap: 4 }}>
          <div className="flex items-baseline justify-between" style={{ gap: 10 }}>
            <h2 className="m-0 truncate" style={{ fontSize: 15.5, fontWeight: 800 }}>{titel}</h2>
            <span className="shrink-0 tabular-nums" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)' }}>{datum}</span>
          </div>
          <span className="truncate" style={{ fontSize: 12.5, color: 'var(--ink2)' }}>{parteien}</span>
          <div className="flex flex-wrap" style={{ gap: 6, marginTop: 4 }}>
            <Chip farbe={farbe}>{ART_LABEL[art]}</Chip>
            {ansicht.status === 'entwurf' ? (
              <>
                <Chip farbe="var(--warn)" icon={NotePencil}>Entwurf</Chip>
                {ansicht.geraet && <Chip farbe="var(--ink2)" flaeche="var(--fill2)" icon={GI ?? undefined}>{ansicht.geraet}</Chip>}
              </>
            ) : ansicht.status === 'abgeschlossen'
              ? <Chip farbe="var(--ok)" icon={CheckCircle}>Abgeschlossen</Chip>
              : <Chip farbe="var(--ink2)" flaeche="var(--fill2)" icon={XCircle}>Abgebrochen</Chip>}
            {ansicht.chips.map((c) => {
              const d = INFO_CHIP[c];
              const tip = c === 'versendet' && versendetAm ? `Per E-Mail versendet am ${versendetAm}`
                : c === 'keine_adresse' ? 'Weder Übergeber noch Übernehmer haben eine E-Mail-Adresse hinterlegt.'
                : c === 'pdf_fehlt' ? 'PDF fehlt – Protokoll ist abgeschlossen, das PDF wurde nicht erzeugt'
                : c === 'nicht_versendet' ? 'Nicht versendet – PDF liegt vor, die E-Mail ging noch nicht raus'
                : c === 'im_dms' ? 'Im DMS – liegt im Aktenschrank' : undefined;
              return <Chip key={c} farbe={d.farbe} icon={d.icon} tip={tip}>{d.label}</Chip>;
            })}
          </div>
        </div>
      </div>

      {zeigeInfo && (
        <div className="flex flex-col" style={{ gap: 6, padding: '10px 14px', borderRadius: 16, background: hinweis ? 'var(--warn-soft)' : 'var(--fill)' }}>
          {ansicht.inErfassung && seit && (
            <span className="flex items-center" style={{ gap: 7, fontSize: 12.5, fontWeight: 600, color: 'var(--ink2)' }}>
              <Clock size={14} weight="fill" className="shrink-0" />Seit {seit}{erfasstVon ? ` · ${erfasstVon}` : ''}
            </span>
          )}
          {ansicht.bereitGrund && (
            <span className="flex items-start" style={{ gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--warn)' }}>
              <WarningCircle size={14} weight="fill" className="mt-[2px] shrink-0" />{ansicht.bereitGrund}
            </span>
          )}
          {veraltetHinweis && (
            <span className="flex items-start" style={{ gap: 7, fontSize: 12.5, fontWeight: 600, color: 'var(--ink2)' }}>
              <Info size={14} weight="fill" className="mt-[2px] shrink-0" />{veraltetHinweis}
            </span>
          )}
        </div>
      )}

      {hausmeister}

      {ansicht.aktionen.length > 0 && <div className="flex flex-wrap" style={{ gap: 8 }}>{ansicht.aktionen.map(knopf)}</div>}
    </article>
  );
}
